/**
 * 菜单管理页面
 */

import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  message,
  Tooltip,
  Dropdown,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  TreeSelect,
  Row,
  Col,
  Tree,
  Badge,
  type MenuProps,
  type TreeDataNode
} from 'antd';
import { modal } from '@/utils/antdStatic';
import type { DataNode } from 'rc-tree/lib/interface';

// 扩展TreeDataNode接口，添加value属性以支持TreeSelect组件
interface CustomTreeDataNode extends DataNode {
  value?: string | number;
  children?: CustomTreeDataNode[];
}
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
  ReloadOutlined,
  ExportOutlined,
  MenuOutlined,
  AppstoreOutlined,
  FolderOutlined,
  FileOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/hooks/useI18n';
import AdvancedProTable from '@/components/common/ProTable/AdvancedProTable';
import { SearchForm } from '@/components/common/SearchForm';
import type { ProTableColumn, ProTableInstance } from '@/components/common/ProTable/AdvancedProTable';
import { menuApi, MenuType, MenuItem } from '@/api/modules/menu';
import { buildTreeData, addChildren } from '@/utils/treeUtils';
const { Option } = Select;
const { TextArea } = Input;

// Using MenuItem and MenuQueryParams interfaces from the API module

const MenuList: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchParams, setSearchParams] = useState<Record<string, any>>({page:-1, limit: -1});
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [currentMenu, setCurrentMenu] = useState<MenuItem | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [menuTreeData, setMenuTreeData] = useState<CustomTreeDataNode[]>([]);
  const tableRef = React.useRef<ProTableInstance>(null);
  const [form] = Form.useForm();

  // 获取菜单数据
  const fetchMenuList = async (params: any) => {
    setLoading(true);
    try {
      // 调用API获取菜单列表
      const response = await menuApi.getMenuTree(params);
      
      if (response && response.data) {
        const rawData = (response.data as any)?.data ?? response.data;
        const menus: MenuItem[] = Array.isArray(rawData) ? rawData : [];
        const flatMenus = addChildren(menus);
        const tree = buildTreeData(menus, 0); // 明确传入0作为顶级菜单的parent_id
        setMenuTreeData(tree);
        
        return {
          data: flatMenus,
          total: flatMenus.length,
          success: true,
        };
      }
      
      return {
        data: [],
        total: 0,
        success: false,
      };
    } catch (error) {
      message.error('获取菜单列表失败');
      return {
        data: [],
        total: 0,
        success: false,
      };
    } finally {
      setLoading(false);
    }
  };


  // 查看菜单详情
  const handleViewDetail = (record: MenuItem) => {
    setCurrentMenu(record);
    setDetailModalVisible(true);
  };

  // 打开表单弹窗
  const handleOpenForm = (mode: 'create' | 'edit', record?: MenuItem, parentId?: string) => {
    setFormMode(mode);
    setCurrentMenu(record || null);
    setFormModalVisible(true);
    parentId = parentId ? parentId : String(record?.id);
    
    if (mode === 'edit' && record) {
      form.setFieldsValue({
        parentId: (record as any).parentId ?? record.parent_id,
        name: record.name,
        title: record.title,
        type: (record as any).menuType ?? record.menu_type,
        path: record.path,
        component: record.component,
        icon: record.icon,
        authority: record.authority,
        sort: (record as any).sortNumber ?? record.sort_order,
        isVisible: record.hide !== 1,
      });
    } else {
      form.resetFields();
      if (parentId) {
        form.setFieldValue('parent_id', parentId);
      }
    }
  };

  // 保存菜单
  const handleSaveMenu = async () => {
    try {
      const values = await form.validateFields();

      if (formMode === 'create') {
        // 创建菜单
        const response = await menuApi.createMenu(values);
        if (response && response.success) {
          message.success('菜单创建成功');
          setFormModalVisible(false);
          tableRef.current?.reload();
        } else {
          message.error('菜单创建失败');
        }
      } else if (currentMenu) {
        // 更新菜单
        const response = await menuApi.updateMenu(currentMenu.id, values);
        if (response && response.success) {
          message.success('菜单更新成功');
          setFormModalVisible(false);
          tableRef.current?.reload();
        } else {
          message.error('菜单更新失败');
        }
      }
    } catch (error) {
      console.error('保存菜单失败:', error);
      message.error('保存失败，请重试');
    }
  };

  // 删除菜单
  const handleDeleteMenu = (record: MenuItem) => {
    // TODO: fix hasChildren logic
    const hasChildren = record.children && record.children.length > 0;
    
    modal.confirm({
      title: '确认删除菜单',
      content: hasChildren 
        ? `菜单"${record.title}"下还有子菜单，删除后将一并删除所有子菜单，确定要继续吗？`
        : `确定要删除菜单"${record.title}"吗？删除后无法恢复。`,
      onOk: async () => {
        try {
          // 调用API删除菜单
          const response = await menuApi.deleteMenu(record.id);
          if (response) {
            message.success('菜单删除成功');
            tableRef.current?.reload();
          } else {
            message.error('菜单删除失败');
          }
        } catch (error) {
          message.error('菜单删除失败');
        }
      },
    });
  };

  // 切换菜单显示状态
  const handleToggleVisible = async (record: MenuItem, checked: boolean) => {
    try {
      // 调用API更新菜单显示状态
      const response = await menuApi.updateMenu(record.id, { hide: checked ? 0 : 1 } as any);
      if (response) {
        message.success(checked ? '菜单已显示' : '菜单已隐藏');
        tableRef.current?.reload();
      } else {
        message.error('状态更新失败');
      }
    } catch (error) {
      message.error('状态更新失败');
    }
  };

  // 调整菜单排序
  const handleMoveMenu = async (record: MenuItem, direction: 'up' | 'down') => {
    try {
      console.log('调整菜单排序:', record.id, direction);
      message.success('排序调整成功');
      tableRef.current?.reload();
    } catch (error) {
      message.error('排序调整失败');
    }
  };

  // 复制菜单
  const handleCopyMenu = (record: MenuItem) => {
    // Create a copy without the id field
    const { id, ...menuDataWithoutId } = record;
    const newMenu = {
      ...menuDataWithoutId,
      name: `${record.name}_copy`,
      title: `${record.title}_副本`,
    } as MenuItem; // Cast as MenuItem to satisfy TypeScript
    
    handleOpenForm('create', newMenu);
  };

  // 获取菜单类型标签
  const getMenuTypeTag = (type: MenuType) => {
    const typeConfig = {
      [MenuType.DIRECTORY]: { color: 'blue', text: '目录', icon: <FolderOutlined /> },
      [MenuType.MENU]: { color: 'green', text: '菜单', icon: <FileOutlined /> },
      [MenuType.BUTTON]: { color: 'orange', text: '按钮', icon: <AppstoreOutlined /> },
    };
    const config = typeConfig[type];
    return <Tag color={config?.color} icon={config?.icon}>{config?.text}</Tag>;
  };

  // 操作菜单
  const getActionMenu = (record: MenuItem): MenuProps => ({
    items: [
      {
        key: 'view',
        label: '查看详情',
        icon: <EyeOutlined />,
        onClick: () => handleViewDetail(record),
      },
      {
        key: 'edit',
        label: '编辑菜单',
        icon: <EditOutlined />,
        onClick: () => handleOpenForm('edit', record),
      },
      {
        key: 'add-child',
        label: '添加子菜单',
        icon: <PlusOutlined />,
        onClick: () => handleOpenForm('create', undefined, String(record.id)),
        disabled: ((record as any).menuType ?? record.menu_type) === MenuType.BUTTON,
      },
      {
        key: 'copy',
        label: '复制菜单',
        icon: <CopyOutlined />,
        onClick: () => handleCopyMenu(record),
      },
      {
        type: 'divider',
      },
      {
        key: 'move-up',
        label: '上移',
        icon: <ArrowUpOutlined />,
        onClick: () => handleMoveMenu(record, 'up'),
      },
      {
        key: 'move-down',
        label: '下移',
        icon: <ArrowDownOutlined />,
        onClick: () => handleMoveMenu(record, 'down'),
      },
      {
        type: 'divider',
      },
      {
        key: 'export',
        label: '导出配置',
        icon: <ExportOutlined />,
        onClick: () => message.info('导出功能开发中'),
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: '删除菜单',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDeleteMenu(record),
      },
    ],
  });

  // 表格列配置
  const columns: ProTableColumn<MenuItem>[] = [
    {
      title: '菜单信息',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      render: (text: string, record: MenuItem) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ marginLeft: (((record as any).parentId ?? record.parent_id) ? 20 : 0) + 'px' }}>
            {record.icon && (
              <span style={{ marginRight: '8px', fontSize: '16px' }}>
                {/* 这里可以根据icon名称渲染对应的图标 */}
                <MenuOutlined />
              </span>
            )}
            <span style={{ fontWeight: 500 }}>{record.title}</span>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {record.name}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'menu_type',
      key: 'menu_type',
      width: 100,
      align: 'center',
      render: (type: MenuType) => getMenuTypeTag(type),
    },
    {
      title: '路径/权限',
      dataIndex: 'path',
      key: 'path',
      width: 200,
      render: (path: string, record: MenuItem) => (
        <div>
          {record.path && (
            <div style={{ marginBottom: '2px', fontSize: '12px' }}>
              路径: {record.path}
            </div>
          )}
          {record.authority && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              权限: {record.authority}
            </div>
          )}
          {record.component && (
            <div style={{ fontSize: '12px', color: '#999' }}>
              组件: {record.component}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
      align: 'center',
      render: (sort: number) => (
        <Badge count={sort} showZero style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (isVisible: boolean, record: MenuItem) => (
        <div>
          <Switch
            size="small"
            checked={isVisible}
            onChange={(checked) => handleToggleVisible(record, checked)}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
          />
          <div style={{ fontSize: '11px', marginTop: '4px', color: '#666' }}>
            {isVisible ? '显示' : '隐藏'}
          </div>
        </div>
      ),
    },
    // {
    //   title: '缓存',
    //   dataIndex: 'is_cache',
    //   key: 'isCache',
    //   width: 80,
    //   align: 'center',
    //   render: (isCache: boolean) => (
    //     <Tag color={isCache ? 'success' : 'default'}>
    //       {isCache ? '是' : '否'}
    //     </Tag>
    //   ),
    // },
    // {
    //   title: '外链',
    //   dataIndex: 'isFrame',
    //   key: 'isFrame',
    //   width: 80,
    //   align: 'center',
    //   render: (isFrame: boolean, record: MenuItem) => (
    //     <div>
    //       <Tag color={isFrame ? 'processing' : 'default'}>
    //         {isFrame ? '是' : '否'}
    //       </Tag>
    //       {isFrame && record.frameUrl && (
    //         <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
    //           {record.frameUrl}
    //         </div>
    //       )}
    //     </div>
    //   ),
    // },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (time: string) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            {time?.split(' ')[0]}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {time?.split(' ')[1]}
          </div>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record: MenuItem) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)} />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenForm('edit', record)} />
          </Tooltip>
          <Tooltip title="添加子菜单">
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleOpenForm('create', undefined, String(record.id))}
              disabled={((record as any).menuType ?? record.menu_type) === MenuType.BUTTON} />
          </Tooltip>
          <Dropdown menu={getActionMenu(record)} trigger={['click']}>
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
      dataIndex: 'id'
    },
  ];

  // 搜索表单配置
  const searchFormFields = [
    {
      name: 'title',
      label: '菜单名称',
      type: 'input' as const,
      placeholder: '请输入菜单名称',
    },
    {
      name: 'name',
      label: '菜单标识',
      type: 'input' as const,
      placeholder: '请输入菜单标识',
    },
    {
      name: 'menu_type',
      label: '菜单类型',
      type: 'select' as const,
      placeholder: '请选择菜单类型',
      options: [
        { label: '目录', value: MenuType.DIRECTORY },
        { label: '菜单', value: MenuType.MENU },
        { label: '按钮', value: MenuType.BUTTON },
      ],
    },
    {
      name: 'status',
      label: '显示状态',
      type: 'select' as const,
      placeholder: '请选择显示状态',
      options: [
        { label: '显示', value: 1 },
        { label: '隐藏', value: 0 },
      ],
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        {/* 搜索表单 */}
        <SearchForm
          fields={searchFormFields}
          onSearch={setSearchParams}
          onReset={() => setSearchParams({})}
        />

        {/* 表格 */}
        <AdvancedProTable<MenuItem>
          ref={tableRef}
          columns={columns}
          request={fetchMenuList}
          params={searchParams}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          toolbar={{
            title: '菜单管理',
            actions: [
              <Button
                key="create"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenForm('create')}
              >
                新建菜单
              </Button>,
            ],
            settings: [
              {
                key: 'reload',
                icon: <ReloadOutlined />,
                tooltip: '刷新',
                onClick: () => tableRef.current?.reload(),
              },
              {
                key: 'export',
                icon: <ExportOutlined />,
                tooltip: '导出',
                onClick: () => message.info('导出功能开发中'),
              },
            ],
          }}
        />
      </Card>

      {/* 菜单详情弹窗 */}
      <Modal
        title="菜单详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            onClick={() => {
              setDetailModalVisible(false);
              handleOpenForm('edit', currentMenu!);
            }}
          >
            编辑菜单
          </Button>,
        ]}
        width={800}
      >
        {currentMenu && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="菜单名称">{currentMenu.title}</Descriptions.Item>
            <Descriptions.Item label="菜单标识">{currentMenu.name}</Descriptions.Item>
            <Descriptions.Item label="菜单类型">{getMenuTypeTag((currentMenu as any).menuType ?? currentMenu.menu_type ?? 1)}</Descriptions.Item>
            <Descriptions.Item label="排序">{(currentMenu as any).sortNumber ?? currentMenu.sort_order}</Descriptions.Item>
            <Descriptions.Item label="路径">{currentMenu.path || '-'}</Descriptions.Item>
            <Descriptions.Item label="组件">{currentMenu.component || '-'}</Descriptions.Item>
            <Descriptions.Item label="图标">{currentMenu.icon || '-'}</Descriptions.Item>
            <Descriptions.Item label="权限标识">{currentMenu.authority || '-'}</Descriptions.Item>
            <Descriptions.Item label="显示状态">
              <Tag color={currentMenu.hide !== 1 ? 'success' : 'error'}>
                {currentMenu.hide !== 1 ? '显示' : '隐藏'}
              </Tag>
            </Descriptions.Item>
            {/* <Descriptions.Item label="缓存">
              <Tag color={currentMenu.isCache ? 'success' : 'default'}>
                {currentMenu.isCache ? '是' : '否'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="外链">
              <Tag color={currentMenu.isFrame ? 'processing' : 'default'}>
                {currentMenu.isFrame ? '是' : '否'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="外链地址">{currentMenu.frameUrl || '-'}</Descriptions.Item> */}
            <Descriptions.Item label="创建时间">{(currentMenu as any).createdAt ?? currentMenu.created_at}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{(currentMenu as any).updatedAt ?? currentMenu.updated_at}</Descriptions.Item>
            <Descriptions.Item label="备注" span={2}>
              {(currentMenu as any).mark || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 菜单表单弹窗 */}
      <Modal
        title={formMode === 'create' ? '新建菜单' : '编辑菜单'}
        open={formModalVisible}
        onCancel={() => setFormModalVisible(false)}
        onOk={handleSaveMenu}
        width={800}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="parent_id" label="上级菜单">
                <TreeSelect
                  placeholder="请选择上级菜单（不选则为顶级菜单）"
                  allowClear
                  treeData={menuTreeData}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="menu_type"
                label="菜单类型"
                rules={[{ required: true, message: '请选择菜单类型' }]}
              >
                <Select placeholder="请选择菜单类型">
                  <Option value={MenuType.DIRECTORY}>目录</Option>
                  <Option value={MenuType.MENU}>菜单</Option>
                  <Option value={MenuType.BUTTON}>按钮</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="菜单标识"
                rules={[
                  { required: true, message: '请输入菜单标识' },
                  { pattern: /^[a-zA-Z0-9_-]+$/, message: '只能包含字母、数字、下划线和短横线' }
                ]}
              >
                <Input placeholder="请输入菜单标识" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="title"
                label="菜单名称"
                rules={[{ required: true, message: '请输入菜单名称' }]}
              >
                <Input placeholder="请输入菜单名称" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
          >
            {({ getFieldValue }) => {
              const menuType = getFieldValue('menu_type');
              return (
                <>
                  {menuType !== MenuType.BUTTON && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="path" label="路由地址">
                          <Input placeholder="请输入路由地址" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="component" label="组件路径">
                          <Input placeholder="请输入组件路径" />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="icon" label="菜单图标">
                <Input placeholder="请输入图标名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="authority" label="权限标识">
                <Input placeholder="请输入权限标识" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="sort_number"
                label="显示排序"
                rules={[{ required: true, message: '请输入排序号' }]}
                initialValue={1}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="status" label="显示状态" valuePropName="checked" initialValue={1}>
                <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="isCache" label="是否缓存" valuePropName="checked">
                <Switch checkedChildren="缓存" unCheckedChildren="不缓存" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="isFrame" label="是否外链" valuePropName="checked">
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isFrame !== currentValues.isFrame}
          >
            {({ getFieldValue }) => {
              const isFrame = getFieldValue('isFrame');
              return isFrame ? (
                <Form.Item
                  name="frameUrl"
                  label="外链地址"
                  rules={[{ required: true, message: '请输入外链地址' }]}
                >
                  <Input placeholder="请输入外链地址" />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuList;